import React, { FC } from 'react';
import { Box, Flex, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import { ILayoutProps } from './BaseLayout';
import { getAnimationProps, squareAnimations } from './animations';

const SquareLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const theme = useTheme();
    const galleryHeadingSize =
        useBreakpointValue({ base: 'sm' as const, lg: 'md' as const }) ?? 'sm';

    const squareBorderColor = colorScheme === 'dark' ? theme.colors.gray[700] : theme.colors.white;

    return (
        <Box position="absolute" width="full" height="full" onClick={onClick}>
            <Box position="relative" width="full" height="full" overflow="hidden">
                {/* Cover image with overlay */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%'
                    }}
                    {...getAnimationProps(squareAnimations.coverImage, enableAnimations)}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </motion.div>

                {/* Overlay */}
                <Box
                    position="absolute"
                    width="full"
                    height="full"
                    background={colorScheme === 'dark' ? 'whiteAlpha.500' : 'blackAlpha.500'}
                />

                {/* Content overlay */}
                <Flex
                    position="absolute"
                    width="full"
                    height="full"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    gap={6}
                    paddingX={4}
                    paddingY={6}
                    textAlign="center"
                >
                    <motion.div
                        {...getAnimationProps(squareAnimations.studioName, enableAnimations)}
                    >
                        <StudioHeading text={brandName} variant={colorScheme} />
                    </motion.div>

                    {/* Square with gallery name */}
                    <motion.div
                        {...getAnimationProps(squareAnimations.galleryName, enableAnimations)}
                    >
                        <Box
                            padding="55px"
                            border={`6px solid ${squareBorderColor}`}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <GalleryHeading
                                size={galleryHeadingSize}
                                text={galleryTitle}
                                variant={colorScheme}
                                textAlign="center"
                            />
                        </Box>
                    </motion.div>

                    <motion.div {...getAnimationProps(squareAnimations.viewBtn, enableAnimations)}>
                        <Button
                            text={buttonText}
                            textLarge={buttonTextLarge}
                            variant={`secondary ${colorScheme}` as const}
                        />
                    </motion.div>
                </Flex>
            </Box>
        </Box>
    );
};

export default SquareLayout;
